import { create } from 'zustand';
import type { UserSummary } from '@/features/auth/types/auth.types';
import { GEO_ACCESS, decodeGeoAccess, type GeoAccess } from '@/features/auth/utils/geo-access';

const AUTH_STORAGE_KEY = 'auth_state';

interface PersistedAuth {
  accessToken: string | null;
  user: UserSummary | null;
  geoAccess: GeoAccess;
  permissions: string[];
  isAdmin: boolean;

  arePermissionsLoaded: boolean;
}

interface AuthState extends PersistedAuth {
  setAuth: (accessToken: string, user: UserSummary) => void;
  setAccessToken: (accessToken: string) => void;
  setPermissions: (permissions: string[], isAdmin: boolean) => void;
  clearAuth: () => void;
}

const EMPTY_AUTH: PersistedAuth = {
  accessToken: null,
  user: null,
  geoAccess: GEO_ACCESS.MKT,
  permissions: [],
  isAdmin: false,
  arePermissionsLoaded: false,
};

function loadPersistedAuth(): PersistedAuth {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return EMPTY_AUTH;
    const parsed = JSON.parse(stored) as Partial<PersistedAuth>;
    return {
      ...EMPTY_AUTH,
      ...parsed,
      geoAccess: parsed.geoAccess ?? decodeGeoAccess(parsed.accessToken ?? null),
    };
  } catch {
    return EMPTY_AUTH;
  }
}

function persistAuth({
  accessToken,
  user,
  geoAccess,
  permissions,
  isAdmin,
  arePermissionsLoaded,
}: PersistedAuth) {
  if (accessToken && user) {
    localStorage.setItem(
      AUTH_STORAGE_KEY,
      JSON.stringify({ accessToken, user, geoAccess, permissions, isAdmin, arePermissionsLoaded }),
    );
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

const persisted = loadPersistedAuth();

export const useAuthStore = create<AuthState>((set) => ({
  ...persisted,
  setAuth: (accessToken, user) => {
    set((state) => {
      const next = {
        accessToken,
        user,
        geoAccess: decodeGeoAccess(accessToken),
        permissions: [],
        isAdmin: false,
        arePermissionsLoaded: false,
      };
      persistAuth({ ...state, ...next });
      return next;
    });
  },
  setAccessToken: (accessToken) => {
    set((state) => {
      if (state.user) persistAuth({ ...state, accessToken });
      return { accessToken };
    });
  },
  setPermissions: (permissions, isAdmin) => {
    set((state) => {
      const next = { permissions, isAdmin, arePermissionsLoaded: true };
      persistAuth({ ...state, ...next });
      return next;
    });
  },
  clearAuth: () => {
    persistAuth(EMPTY_AUTH);
    set(EMPTY_AUTH);
  },
}));
